import React from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { usePageContext } from "../../context/PageContext";
import { LinkConnector } from "../LinkConnector";
import { Footer } from "../../components/Footer";
import { languageToggleAdapter } from "../../components/LanguageToggle";
import { LanguageToggleConnector } from "../LanguageToggleConnector";
import { RCL as useTranslation } from "../../components/RCL";
import { formatSvgUrl } from "../../utils/svgUrl";

const FooterConnector = ({ data, altLangPages, footer }) => {
	const pageFooter = footer || data.defaultFooter;

	const { locale } = usePageContext();

	const dictionary = {
		"vwq-logo-title": useTranslation({ searchKey: "vwq-logo-title" }),
		selectLanguage: useTranslation({ searchKey: "languagePreference" }),
		socialAppLinksTitle: useTranslation({ searchKey: "social-app-links-title" }),
	};

	const columns = [];

	const processMenu = columnList => {
		const menuData = [];
		const linkTotal = columnList.menuItems?.length;

		// Assess if a link component
		const linkArray = columnList.menuItems?.reduce((accumulator, item) => {
			if (item?.type === "link") {
				accumulator.push({
					id: item.id,
					url: item.url,
					title: item.linkText,
					target: item.target,
				});
			}
			return accumulator;
		}, []);

		if (linkTotal === linkArray?.length) {
			menuData.push({
				id: columnList.id,
				title: columnList.displayTitle,
				links: linkArray,
			});
		}

		return menuData;
	};

	pageFooter.menuLinks?.forEach(list => {
		let menuItems = processMenu(list);

		if (menuItems?.length <= 0) {
			menuItems = list.menuItems.map(menuItem => processMenu(menuItem));

			if (menuItems?.length) {
				menuItems = menuItems.flat();

				// Ignore any nested links or empty menus by filtering them out
				columns.push(menuItems.filter(item => item?.links));
			}
		} else {
			columns.push(...menuItems);
		}
	});

	return (
		<Footer
			className="page__footer"
			locale={locale}
			labels={{
				selectLanguage: dictionary.selectLanguage,
				socialAppLinksTitle: dictionary.socialAppLinksTitle,
			}}
			columns={columns}
			altLangPages={languageToggleAdapter(altLangPages)}
			renderLanguageToggle={(_altLangPages, { classNames, locale: _locale, labels }) => (
				<LanguageToggleConnector
					altLangPages={_altLangPages}
					classNames={classNames}
					locale={_locale}
					variant="drop-down"
					labels={labels}
				/>
			)}
			logoData={{
				logoMessage: pageFooter.logoMessage,
				...(pageFooter.logo?.[0]
					? {
							logoImage: {
								...pageFooter.logo?.[0],
								alt: dictionary["vwq-logo-title"],
							},
					  }
					: {}),
			}}
			socialLinks={pageFooter.socialLinks?.map(link => ({
				...link,
				image: link.image?.map(image => ({ ...image, secure_url: formatSvgUrl(image) })),
			}))}
			appStoreLinks={pageFooter.appStoreLinks?.map(link => ({
				...link,
				image: link.image?.map(image => ({ ...image, secure_url: formatSvgUrl(image) })),
			}))}
			copyrightText={pageFooter.footerCopyright?.replace("{{date}}", new Date().getFullYear())}
			renderLinks={(item, className) => (
				<LinkConnector
					to={item.url}
					target={item.target}
					className={className}
					icon={item.icon}
					// Force LinkConnector to render `children` and not use the ImageConnector (which has no svg support)
					// TODO: Review underlying implementation (and test against other SVGs loaded via Cloudinary)
					image={item.image?.[0]?.format === "svg" ? null : item.image}
					showExternalIcon={item.showExternalIcon}>
					{item.title}
				</LinkConnector>
			)}
			renderImage={(image, { className }) => (
				<img
					className={className}
					src={image.src || image.secure_url}
					alt={image.alt}
					width={image.width}
					height={image.height}
				/>
			)}
		/>
	);
};

FooterConnector.propTypes = {
	data: PropTypes.object.isRequired,
	footer: PropTypes.shape({
		menuLinks: PropTypes.arrayOf(
			PropTypes.shape({
				displayTitle: PropTypes.string,
				id: PropTypes.string,
				menuItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
			})
		),
		footerCopyright: PropTypes.string,
		logoMessage: PropTypes.string,
		logo: PropTypes.arrayOf(
			PropTypes.shape({
				height: PropTypes.number,
				width: PropTypes.number,
				secure_url: PropTypes.string,
			})
		),
		socialLinks: PropTypes.arrayOf(PropTypes.object),
		appStoreLinks: PropTypes.arrayOf(PropTypes.object),
	}),
	altLangPages: PropTypes.shape({
		id: PropTypes.string,
		lang: PropTypes.string,
		path: PropTypes.string,
	}).isRequired,
};

FooterConnector.defaultProps = {
	footer: undefined,
};

export const query = graphql`
	query GetDefaultLocalizedFooter($locale: String!) {
		defaultFooter: contentfulFooter(
			contentful_id: { eq: "1wP5o1sJkGFOdKG7tN1sYO" }
			node_locale: { eq: $locale }
		) {
			...Footer
		}
	}
`;

export default FooterConnector;
export { FooterConnector };
